import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import supabase from '../utils/supabaseClient';
import SimpleEditorField from './SimpleEditorField';
import './ExerciseEditor.css';

const EXERCISE_TYPES = ['multiple_choice', 'numeric_input', 'free_response', 'true_false'];
const STATUS_OPTIONS = ['draft', 'under_review', 'approved'];
const DEFAULT_OPTIONS = [
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
];

const extractMissingColumn = (message = '') => {
    const schemaCacheMatch = message.match(/Could not find the '([^']+)' column/i);
    if (schemaCacheMatch?.[1]) return schemaCacheMatch[1];

    const postgresMatch = message.match(/column\s+"([^"]+)"\s+does not exist/i);
    if (postgresMatch?.[1]) return postgresMatch[1];

    return null;
};

const persistWithSchemaFallback = async ({ table, isEditing, id, payload }) => {
    let currentPayload = { ...payload };
    const removedColumns = [];

    for (let attempt = 0; attempt < 8; attempt += 1) {
        const query = isEditing
            ? supabase.from(table).update(currentPayload).eq('id', id)
            : supabase.from(table).insert(currentPayload);

        const { error } = await query;
        if (!error) {
            return { removedColumns };
        }

        const missingColumn = extractMissingColumn(error?.message || '');
        if (missingColumn && Object.prototype.hasOwnProperty.call(currentPayload, missingColumn)) {
            removedColumns.push(missingColumn);
            delete currentPayload[missingColumn];
            continue;
        }

        throw error;
    }

    return { removedColumns };
};

const ExerciseEditor = () => {
    const { isDarkTheme } = useTheme();
    const { user, profile } = useAuth();
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const isLesson = searchParams.get('type') === 'lesson';
    const isEditing = Boolean(id);

    const [saving, setSaving] = useState(false);
    const [copying, setCopying] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });

    // Form fields
    const [title, setTitle] = useState('');
    const [unitId, setUnitId] = useState('');
    const [exerciseType, setExerciseType] = useState('multiple_choice');
    const [comments, setComments] = useState('');
    const [tags, setTags] = useState([]);
    const [tagInput, setTagInput] = useState('');
    const [language, setLanguage] = useState('en');
    const [isTranslated, setIsTranslated] = useState(false);
    const [authorName, setAuthorName] = useState(profile?.display_name || '');
    const [status, setStatus] = useState('draft');

    // Content sections
    const [frontContent, setFrontContent] = useState('');
    const [frontImages, setFrontImages] = useState([]);
    const [hintContent, setHintContent] = useState('');
    const [hintImages, setHintImages] = useState([]);
    const [backContent, setBackContent] = useState('');
    const [backImages, setBackImages] = useState([]);

    // Answer options
    const [answerType, setAnswerType] = useState('multiple_choice');
    const [options, setOptions] = useState(DEFAULT_OPTIONS);
    const [numericAnswer, setNumericAnswer] = useState('');

    // Units list
    const [units, setUnits] = useState([]);

    // Preview toggle
    const [showPreview, setShowPreview] = useState(false);

    useEffect(() => {
        const fetchUnits = async () => {
            const { data } = await supabase
                .from('units')
                .select('id, title')
                .order('title', { ascending: true });
            if (data) setUnits(data);
        };
        fetchUnits();
    }, []);

    const loadExercise = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const table = isLesson ? 'lessons' : 'exercises';
            const { data, error } = await supabase
                .from(table)
                .select('*')
                .eq('id', id)
                .maybeSingle();

            if (error) throw error;
            if (!data) {
                setMessage({ text: 'Item not found.', type: 'error' });
                return;
            }

            setTitle(data.title || '');
            setUnitId(data.unit_id || '');
            setExerciseType(data.exercise_type || 'multiple_choice');
            setComments(data.comments || '');
            setTags(Array.isArray(data.tags) ? data.tags : []);
            setLanguage(data.language || 'en');
            setIsTranslated(data.is_translated || false);
            setAuthorName(data.author_name || '');
            setStatus(data.status || 'draft');
            setFrontContent(data.front_content || '');
            setFrontImages(Array.isArray(data.front_images) ? data.front_images : []);
            setHintContent(data.hint_content || '');
            setHintImages(Array.isArray(data.hint_images) ? data.hint_images : []);
            setBackContent(data.back_content || '');
            setBackImages(Array.isArray(data.back_images) ? data.back_images : []);
            setAnswerType(data.answer_type || 'multiple_choice');
            setOptions(Array.isArray(data.options) ? data.options : DEFAULT_OPTIONS);
            setNumericAnswer(data.numeric_answer || '');
        } catch (err) {
            console.error('[ExerciseEditor] Load error:', err);
            setMessage({ text: 'Failed to load item.', type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [id, isLesson]);

    useEffect(() => {
        loadExercise();
    }, [loadExercise]);

    const handleAddTag = () => {
        const trimmed = tagInput.trim();
        if (trimmed && !tags.includes(trimmed)) {
            setTags([...tags, trimmed]);
            setTagInput('');
        }
    };

    const handleRemoveTag = (tag) => {
        setTags(tags.filter(t => t !== tag));
    };

    const handleTagKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const handleOptionChange = (index, field, value) => {
        const updated = options.map((opt, i) => {
            if (i === index) {
                return { ...opt, [field]: value };
            }
            if (field === 'isCorrect' && value === true) {
                return { ...opt, isCorrect: false };
            }
            return opt;
        });
        setOptions(updated);
    };

    const handleAddOption = () => {
        setOptions([...options, { text: '', isCorrect: false }]);
    };

    const handleRemoveOption = (index) => {
        if (options.length <= 2) return;
        setOptions(options.filter((_, i) => i !== index));
    };

    const handleImageUpload = async (e, section) => {
        const file = e.target.files?.[0];
        if (!file || !user) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            setMessage({ text: 'Invalid file type. Only JPEG, PNG, GIF, WebP, and SVG are allowed.', type: 'error' });
            return;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setMessage({ text: 'File too large. Maximum size is 5MB.', type: 'error' });
            return;
        }

        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
        const fileExt = file.name.split('.').pop().toLowerCase();
        if (!allowedExtensions.includes(fileExt)) {
            setMessage({ text: 'Invalid file extension.', type: 'error' });
            return;
        }

        try {
            const fileName = `${user.id}/${Date.now()}.${fileExt}`;

            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('exercise-images')
                .upload(fileName, file, {
                    cacheControl: '3600',
                    upsert: true,
                    contentType: file.type,
                });

            if (uploadError) throw uploadError;

            const { data: publicData } = supabase.storage
                .from('exercise-images')
                .getPublicUrl(uploadData?.path || fileName);

            const url = publicData.publicUrl;

            if (section === 'front') setFrontImages(prev => [...prev, url]);
            else if (section === 'hint') setHintImages(prev => [...prev, url]);
            else if (section === 'back') setBackImages(prev => [...prev, url]);
        } catch (err) {
            console.error('[ExerciseEditor] Image upload error:', err);
            setMessage({ text: 'Failed to upload image.', type: 'error' });
        }
    };

    const handleRemoveImage = (section, index) => {
        if (section === 'front') setFrontImages(prev => prev.filter((_, i) => i !== index));
        else if (section === 'hint') setHintImages(prev => prev.filter((_, i) => i !== index));
        else if (section === 'back') setBackImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setMessage({ text: '', type: '' });

        try {
            const table = isLesson ? 'lessons' : 'exercises';
            const payload = {
                title,
                unit_id: unitId || null,
                comments,
                tags,
                language,
                is_translated: isTranslated,
                author_name: authorName,
                author_id: user?.id || null,
                status,
                front_content: frontContent,
                front_images: frontImages,
                hint_content: hintContent,
                hint_images: hintImages,
                back_content: backContent,
                back_images: backImages,
                updated_at: new Date().toISOString(),
            };

            // Only add exercise-specific fields when saving an exercise
            if (!isLesson) {
                payload.exercise_type = exerciseType;
                payload.answer_type = answerType;
                payload.options = (answerType === 'multiple_choice' || answerType === 'true_false') ? options : null;
                payload.numeric_answer = answerType === 'numeric_input' ? numericAnswer : null;
            }

            if (!isEditing) {
                payload.created_at = new Date().toISOString();
            }

            const { removedColumns } = await persistWithSchemaFallback({
                table,
                isEditing,
                id,
                payload,
            });

            setMessage({
                text: `${isLesson ? 'Lesson' : 'Exercise'} ${isEditing ? 'updated' : 'created'} successfully!${removedColumns.length > 0 ? ` (Skipped unsupported fields: ${removedColumns.join(', ')})` : ''}`,
                type: 'success',
            });

            if (!isEditing) {
                setTimeout(() => navigate('/admin/exercises'), 1000);
            }
        } catch (err) {
            console.error('[ExerciseEditor] Save error:', err);
            setMessage({ text: err.message || 'Failed to save.', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleCopyLastEdited = async () => {
        if (!unitId) {
            setMessage({ text: 'Please select a unit first.', type: 'error' });
            return;
        }

        setCopying(true);
        setMessage({ text: '', type: '' });

        try {
            const table = isLesson ? 'lessons' : 'exercises';
            let query = supabase
                .from(table)
                .select('*')
                .eq('unit_id', unitId)
                .order('updated_at', { ascending: false })
                .limit(1);

            if (!isLesson) {
                query = query.eq('exercise_type', exerciseType);
            }

            if (isEditing && id) {
                query = query.neq('id', id);
            }

            const { data, error } = await query;
            if (error) throw error;

            const source = Array.isArray(data) ? data[0] : null;
            if (!source) {
                setMessage({
                    text: `No previous ${isLesson ? 'lesson' : 'exercise'} found for this unit${isLesson ? '' : ' and type'}.`,
                    type: 'error',
                });
                return;
            }

            setTitle(source.title || '');
            setComments(source.comments || '');
            setTags(Array.isArray(source.tags) ? source.tags : []);
            setLanguage(source.language || 'en');
            setIsTranslated(Boolean(source.is_translated));
            setAuthorName(source.author_name || profile?.display_name || '');
            setStatus(source.status || 'draft');
            setFrontContent(source.front_content || '');
            setFrontImages(Array.isArray(source.front_images) ? source.front_images : []);
            setHintContent(source.hint_content || '');
            setHintImages(Array.isArray(source.hint_images) ? source.hint_images : []);
            setBackContent(source.back_content || '');
            setBackImages(Array.isArray(source.back_images) ? source.back_images : []);

            if (!isLesson) {
                setExerciseType(source.exercise_type || exerciseType);
                setAnswerType(source.answer_type || 'multiple_choice');
                setOptions(Array.isArray(source.options) ? source.options : DEFAULT_OPTIONS);
                setNumericAnswer(source.numeric_answer || '');
            }

            setMessage({ text: `Copied data from last edited ${isLesson ? 'lesson' : 'exercise'}.`, type: 'success' });
        } catch (err) {
            console.error('[ExerciseEditor] Copy last edited error:', err);
            setMessage({ text: 'Failed to copy last edited item.', type: 'error' });
        } finally {
            setCopying(false);
        }
    };

    const renderContentSection = (label, content, setContent, images, sectionKey) => (
        <div className="editor-content-section">
            <div className="editor-content-header">
                <h3>{label}</h3>
                <span className="editor-content-hint">Supports LaTeX: wrap in $...$ or $$...$$</span>
            </div>
            <SimpleEditorField
                value={content}
                onChange={setContent}
                placeholder={`Write ${label.toLowerCase()} content here. Use $...$ for inline LaTeX, $$...$$ for display LaTeX.`}
                minHeight={210}
            />
            <div className="editor-images-row">
                {images.map((url, index) => (
                    <div key={index} className="editor-image-thumb">
                        <img src={url} alt={`${label} ${index + 1}`} />
                        <button
                            type="button"
                            className="editor-image-remove"
                            onClick={() => handleRemoveImage(sectionKey, index)}
                        >
                            ×
                        </button>
                    </div>
                ))}
                <label className="editor-image-upload">
                    <span>+ Image</span>
                    <input
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => handleImageUpload(e, sectionKey)}
                    />
                </label>
            </div>
        </div>
    );

    if (loading) {
        return <div className="admin-loading">Loading...</div>;
    }

    return (
        <div className={`exercise-editor ${isDarkTheme ? 'dark' : 'light'}`}>
            {message.text && (
                <div className={`editor-message ${message.type}`}>
                    {message.text}
                </div>
            )}

            <form onSubmit={handleSubmit} className="editor-form">
                <div className="editor-top-bar">
                    <h2>{isEditing ? 'Edit' : 'Create'} {isLesson ? 'Lesson' : 'Exercise'}</h2>
                    <div className="editor-top-actions">
                        <button
                            type="button"
                            className={`editor-preview-btn ${showPreview ? 'active' : ''}`}
                            onClick={() => setShowPreview(!showPreview)}
                        >
                            {showPreview ? 'Hide Preview' : 'Preview'}
                        </button>
                        <button
                            type="button"
                            className="editor-copy-btn"
                            onClick={handleCopyLastEdited}
                            disabled={copying || saving}
                        >
                            {copying ? 'Copying...' : `Copy last edited ${isLesson ? 'lesson' : 'exercise'}`}
                        </button>
                        <select
                            className="editor-status-select"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                        >
                            {STATUS_OPTIONS.map(s => (
                                <option key={s} value={s}>
                                    {s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {showPreview && (
                    <div className="editor-preview card">
                        <h4>Preview</h4>
                        <div className="editor-preview-content">
                            <strong>Front:</strong>
                            <div dangerouslySetInnerHTML={{ __html: frontContent || '(empty)' }} />
                            {hintContent && (
                                <>
                                    <strong>Hint:</strong>
                                    <div dangerouslySetInnerHTML={{ __html: hintContent }} />
                                </>
                            )}
                            <strong>Back (Solution):</strong>
                            <div dangerouslySetInnerHTML={{ __html: backContent || '(empty)' }} />
                        </div>
                    </div>
                )}

                <div className="editor-grid">
                    <div className="editor-main-col">
                        {/* Title */}
                        <div className="editor-field card">
                            <label htmlFor="exercise-title">Title</label>
                            <input
                                id="exercise-title"
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={`${isLesson ? 'Lesson' : 'Exercise'} title`}
                                required
                            />
                        </div>

                        {/* Front Section */}
                        <div className="card">
                            {renderContentSection('Front (Statement)', frontContent, setFrontContent, frontImages, 'front')}
                        </div>

                        {/* Answer Options - only for exercises */}
                        {!isLesson && (
                            <div className="editor-answers card">
                                <h3>Answer Options</h3>
                                <div className="editor-answer-type-row">
                                    <label>Answer type:</label>
                                    <select
                                        value={answerType}
                                        onChange={(e) => setAnswerType(e.target.value)}
                                    >
                                        <option value="multiple_choice">Multiple Choice</option>
                                        <option value="numeric_input">Numeric Input</option>
                                        <option value="free_response">Free Response</option>
                                        <option value="true_false">True / False</option>
                                    </select>
                                </div>

                                {(answerType === 'multiple_choice' || answerType === 'true_false') && (
                                    <div className="editor-options-list">
                                        {options.map((opt, index) => (
                                            <div key={index} className="editor-option-row">
                                                <input
                                                    type="radio"
                                                    name="correct-option"
                                                    checked={opt.isCorrect}
                                                    onChange={() => handleOptionChange(index, 'isCorrect', true)}
                                                />
                                                <input
                                                    type="text"
                                                    className="editor-option-input"
                                                    value={opt.text}
                                                    onChange={(e) => handleOptionChange(index, 'text', e.target.value)}
                                                    placeholder={`Option ${index + 1}`}
                                                />
                                                {options.length > 2 && (
                                                    <button
                                                        type="button"
                                                        className="editor-option-remove"
                                                        onClick={() => handleRemoveOption(index)}
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                        {answerType === 'multiple_choice' && (
                                            <button
                                                type="button"
                                                className="editor-add-option"
                                                onClick={handleAddOption}
                                            >
                                                + Add Option
                                            </button>
                                        )}
                                    </div>
                                )}

                                {answerType === 'numeric_input' && (
                                    <div className="editor-numeric-answer">
                                        <label>Correct numeric answer:</label>
                                        <input
                                            type="text"
                                            value={numericAnswer}
                                            onChange={(e) => setNumericAnswer(e.target.value)}
                                            placeholder="e.g. 42"
                                        />
                                    </div>
                                )}

                                {answerType === 'free_response' && (
                                    <p className="editor-hint-text">
                                        Free response answers will be reviewed manually.
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Hint Section */}
                        <div className="card">
                            {renderContentSection('Hint', hintContent, setHintContent, hintImages, 'hint')}
                        </div>

                        {/* Back Section */}
                        <div className="card">
                            {renderContentSection('Back (Solution)', backContent, setBackContent, backImages, 'back')}
                        </div>
                    </div>

                    <div className="editor-side-col">
                        {/* Metadata */}
                        <div className="editor-meta card">
                            <h3>Details</h3>

                            <div className="editor-field">
                                <label htmlFor="unit-select">Unit</label>
                                <select
                                    id="unit-select"
                                    value={unitId}
                                    onChange={(e) => setUnitId(e.target.value)}
                                >
                                    <option value="">Select a unit</option>
                                    {units.map(u => (
                                        <option key={u.id} value={u.id}>{u.title}</option>
                                    ))}
                                </select>
                            </div>

                            {!isLesson && (
                                <div className="editor-field">
                                    <label htmlFor="type-select">Exercise Type</label>
                                    <select
                                        id="type-select"
                                        value={exerciseType}
                                        onChange={(e) => setExerciseType(e.target.value)}
                                    >
                                        {EXERCISE_TYPES.map(t => (
                                            <option key={t} value={t}>
                                                {t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="editor-field">
                                <label htmlFor="language-select">Language</label>
                                <select
                                    id="language-select"
                                    value={language}
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    <option value="en">English</option>
                                    <option value="fr">French</option>
                                    <option value="it">Italian</option>
                                    <option value="ar">Arabic</option>
                                </select>
                            </div>

                            <div className="editor-field editor-checkbox-field">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={isTranslated}
                                        onChange={(e) => setIsTranslated(e.target.checked)}
                                    />
                                    Translated to another language
                                </label>
                            </div>

                            <div className="editor-field">
                                <label htmlFor="author-name">Author</label>
                                <input
                                    id="author-name"
                                    type="text"
                                    value={authorName}
                                    onChange={(e) => setAuthorName(e.target.value)}
                                    placeholder="Author name"
                                />
                            </div>
                        </div>

                        {/* Tags */}
                        <div className="editor-tags-card card">
                            <h3>Tags</h3>
                            <div className="editor-tags-list">
                                {tags.map((tag, i) => (
                                    <span key={i} className="editor-tag">
                                        {tag}
                                        <button type="button" onClick={() => handleRemoveTag(tag)}>×</button>
                                    </span>
                                ))}
                            </div>
                            <div className="editor-tag-input-row">
                                <input
                                    type="text"
                                    value={tagInput}
                                    onChange={(e) => setTagInput(e.target.value)}
                                    onKeyDown={handleTagKeyDown}
                                    placeholder="Add a tag"
                                />
                                <button type="button" onClick={handleAddTag} className="editor-tag-add-btn">
                                    Add
                                </button>
                            </div>
                        </div>

                        {/* Comments */}
                        <div className="editor-comments card">
                            <h3>Comments</h3>
                            <SimpleEditorField
                                value={comments}
                                onChange={setComments}
                                placeholder="Notes, clarifications, image placement instructions..."
                                minHeight={120}
                            />
                        </div>
                    </div>
                </div>

                <div className="editor-form-actions">
                    <button
                        type="submit"
                        className="admin-action-btn primary"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
                    </button>
                    <button
                        type="button"
                        className="admin-action-btn secondary"
                        onClick={() => navigate('/admin/exercises')}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ExerciseEditor;
